/**
 * Hook to handle MCP commands for a table explorer tab.
 *
 * Subscribes to the MCP command store and executes commands for this tab.
 */

import { useEffect, useCallback } from 'react';
import { useTableExplorerMcpStore, type TableExplorerMcpCommand } from '../store/tableExplorerMcpStore';
import type { ChartConfig } from '../../charts/types/chartTypes';

type TableExplorerMcpCommandHandlers = {
  onRefresh: () => void;
  onShowChart: (show: boolean) => void;
  onSetChartConfig: (config: ChartConfig) => void;
  onSelectRow: (rowIndex: number) => void;
  onShowRowDetails: (show: boolean, layout: 'bottom' | 'side', rowIndex?: number) => void;
};

export function useTableExplorerMcpCommands(
  tabId: string,
  handlers: TableExplorerMcpCommandHandlers
) {
  const completeCommand = useTableExplorerMcpStore((state) => state.completeCommand);
  const pendingCommands = useTableExplorerMcpStore((state) => state.pendingCommands);

  const executeCommand = useCallback(
    (command: TableExplorerMcpCommand) => {
      switch (command.type) {
        case 'refresh':
          handlers.onRefresh();
          break;
        case 'show_chart':
          handlers.onShowChart(command.show);
          break;
        case 'set_chart_config':
          handlers.onSetChartConfig(command.config);
          break;
        case 'select_row':
          handlers.onSelectRow(command.rowIndex);
          break;
        case 'show_row_details':
          handlers.onShowRowDetails(command.show, command.layout || 'side', command.rowIndex);
          break;
      }
    },
    [handlers]
  );

  // Process pending commands when they arrive
  useEffect(() => {
    const commands = pendingCommands.filter((cmd) => cmd.tabId === tabId);
    for (const pending of commands) {
      executeCommand(pending.command);
      completeCommand(pending.id);
    }
  }, [tabId, pendingCommands, completeCommand, executeCommand]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      useTableExplorerMcpStore.getState().clearTabCommands(tabId);
    };
  }, [tabId]);
}
